'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { getRetailers } from '@/lib/retailerStore';

interface RetailStore {
  name: string;
  address: string;
  pincode: string;
  city: string;
  phone?: string;
}

interface Platform {
  id: string;
  name: string;
  tagline: string;
  eta: string;
  href: string;
  color: string;
  textColor: string;
  available: boolean;
  category: 'quick' | 'ecommerce';
  initial: string;
}

const platforms: Platform[] = [
  {
    id: 'blinkit',
    name: 'Blinkit',
    tagline: '10-minute grocery delivery',
    eta: '~10 min',
    category: 'quick',
    href: 'https://blinkit.com/',
    color: '#F8CB46',
    textColor: '#1A0F05',
    available: true,
    initial: 'B',
  },
  {
    id: 'instamart',
    name: 'Swiggy Instamart',
    tagline: "Swiggy's instant grocery",
    eta: '15–30 min',
    category: 'quick',
    href: 'https://www.swiggy.com/instamart',
    color: '#FC8019',
    textColor: '#fff',
    available: true,
    initial: 'I',
  },
  {
    id: 'amazon',
    name: 'Amazon India',
    tagline: 'Prime delivery, best prices',
    eta: 'Same / next day',
    category: 'ecommerce',
    href: 'https://www.amazon.in/s?srs=93889452031',
    color: '#FF9900',
    textColor: '#111',
    available: true,
    initial: 'a',
  },
  {
    id: 'flipkart',
    name: 'Flipkart',
    tagline: 'Assured quality, great deals',
    eta: '1–3 days',
    category: 'ecommerce',
    href: 'https://www.flipkart.com/food-products/ready-meal-mixes/ready-mix-meals/kasturi~brand/pr?sid=eat,xn7,kzo&marketplace=FLIPKART&otracker=product_breadCrumbs_KASTURI+Ready+Mix+Meals',
    color: '#2874F0',
    textColor: '#fff',
    available: true,
    initial: 'F',
  },
];

interface WhereToBuyModalProps {
  open: boolean;
  onClose: () => void;
  productName?: string;
}

type Step = 'pincode' | 'results';

export default function WhereToBuyModal({ open, onClose, productName }: WhereToBuyModalProps) {
  const [step, setStep] = useState<Step>('pincode');
  const [pincode, setPincode] = useState('');
  const [pincodeError, setPincodeError] = useState('');
  const [foundStores, setFoundStores] = useState<RetailStore[]>([]);
  const [searchedPin, setSearchedPin] = useState('');

  const quickPlatforms = platforms.filter((p) => p.category === 'quick' && p.available);
  const ecommercePlatforms = platforms.filter((p) => p.category === 'ecommerce' && p.available);
  const comingSoonPlatforms = platforms.filter((p) => !p.available);

  const hasQuickCommerce = quickPlatforms.length > 0;
  const hasEcommerce = ecommercePlatforms.length > 0;

  const handleClose = useCallback(() => {
    onClose();
    setTimeout(() => {
      setStep('pincode');
      setPincode('');
      setPincodeError('');
      setFoundStores([]);
      setSearchedPin('');
    }, 300);
  }, [onClose]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    if (open) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, handleClose]);

  const handlePincodeSearch = () => {
    const trimmed = pincode.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setPincodeError('Please enter a valid 6-digit PIN code.');
      return;
    }
    setPincodeError('');
    const retailStores = getRetailers();
    const results = retailStores.filter((s) => s.pincode === trimmed);
    setFoundStores(results);
    setSearchedPin(trimmed);
    setStep('results');
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center p-4"
      style={{ background: 'rgba(26,18,8,0.72)', backdropFilter: 'blur(8px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label="Where to Buy Kasturi"
    >
      <div
        className="relative w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl"
        style={{ background: 'var(--background)', border: '1px solid var(--border)' }}
      >
        {/* Header */}
        <div className="relative px-7 pt-7 pb-5" style={{ background: 'var(--primary)' }}>
          <div
            className="absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
              backgroundSize: '128px 128px',
            }}
          />
          <p className="text-[10px] uppercase tracking-[0.25em] mb-1 relative z-10" style={{ color: 'rgba(253,248,242,0.6)' }}>
            {productName ? productName : 'Kasturi Products'}
          </p>
          <h2 className="font-display text-2xl font-[200] relative z-10 leading-tight" style={{ color: 'var(--primary-foreground)' }}>
            {step === 'pincode' ? 'Where to Buy' : 'Available Near You'}
          </h2>
          <p className="text-xs mt-1 relative z-10" style={{ color: 'rgba(253,248,242,0.7)' }}>
            {step === 'pincode' ?'Enter your PIN code to see the best buying options in your area.'
              : `Showing options for PIN code ${searchedPin} — we never collect payments here.`}
          </p>
        </div>

        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-20 w-8 h-8 flex items-center justify-center rounded-full transition-colors"
          style={{ background: 'rgba(253,248,242,0.18)', color: 'var(--primary-foreground)' }}
          aria-label="Close"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        {/* Body */}
        <div className="px-7 py-6">

          {/* ── STEP 1: PIN CODE ENTRY ── */}
          {step === 'pincode' && (
            <div>
              <p className="text-sm mb-5 leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                Your PIN code helps us show you the fastest delivery option — quick commerce, online stores, or a nearby retailer.
              </p>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={pincode}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    setPincode(val);
                    if (pincodeError) setPincodeError('');
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handlePincodeSearch(); }}
                  placeholder="Enter 6-digit PIN code"
                  className="flex-1 px-4 py-3.5 rounded-xl text-sm font-mono font-semibold outline-none transition-all duration-200"
                  style={{
                    background: 'var(--secondary)',
                    border: `1.5px solid ${pincodeError ? '#D32328' : 'var(--border)'}`,
                    color: 'var(--foreground)',
                  }}
                  autoFocus
                />
                <button
                  onClick={handlePincodeSearch}
                  className="px-5 py-3.5 rounded-xl text-sm font-bold uppercase tracking-wider transition-all duration-200 hover:opacity-90"
                  style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
                >
                  Go
                </button>
              </div>
              {pincodeError && (
                <p className="text-xs mb-1" style={{ color: '#D32328' }}>{pincodeError}</p>
              )}
              <p className="text-[11px] mt-3" style={{ color: 'var(--muted-foreground)' }}>
                We&apos;ll show quick commerce first, then Amazon/Flipkart, and if needed — the nearest Kasturi retailer.
              </p>
            </div>
          )}

          {/* ── STEP 2: RESULTS ── */}
          {step === 'results' && (
            <div className="flex flex-col gap-5">

              {/* Quick Commerce — shown first if available */}
              {hasQuickCommerce && (
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--muted-foreground)' }}>
                    <span>⚡</span> Fastest — Quick Commerce
                  </p>
                  <div className="flex flex-col gap-2">
                    {quickPlatforms.map((p) => (
                      <a
                        key={p.id}
                        href={p.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between px-4 py-3.5 rounded-xl font-semibold text-sm transition-all duration-200 hover:opacity-90 hover:scale-[1.015]"
                        style={{ background: p.color, color: p.textColor }}
                      >
                        <div className="flex items-center gap-3">
                          <span className="font-display font-black text-xl w-8 text-center" style={{ color: p.textColor, opacity: 0.9 }}>{p.initial}</span>
                          <div>
                            <p className="font-bold text-sm leading-tight">{p.name}</p>
                            <p className="text-[11px] opacity-70 font-normal">{p.tagline}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-semibold opacity-70 hidden sm:block">{p.eta}</span>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M7 17L17 7M17 7H7M17 7v10" />
                          </svg>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Coming soon quick commerce — shown as greyed out if none available */}
              {!hasQuickCommerce && comingSoonPlatforms.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--muted-foreground)' }}>
                    <span>⚡</span> Quick Commerce — Coming Soon
                  </p>
                  <div className="flex gap-2 mb-1">
                    {comingSoonPlatforms.map((p) => (
                      <div
                        key={p.id}
                        className="flex-1 flex items-center gap-2 px-3 py-3 rounded-xl opacity-45"
                        style={{ background: 'var(--secondary)', border: '1px dashed var(--border)' }}
                      >
                        <span className="font-display font-black text-lg w-7 text-center" style={{ color: p.color }}>{p.initial}</span>
                        <div>
                          <p className="font-bold text-xs" style={{ color: 'var(--foreground)' }}>{p.name}</p>
                          <p className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>Coming soon</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Ecommerce — Amazon / Flipkart */}
              {hasEcommerce && (
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--muted-foreground)' }}>
                    <span>🛒</span> Order Online
                  </p>
                  <div className="flex flex-col gap-2">
                    {ecommercePlatforms.map((p) => (
                      <a
                        key={p.id}
                        href={p.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between px-4 py-3.5 rounded-xl font-semibold text-sm transition-all duration-200 hover:opacity-90 hover:scale-[1.015]"
                        style={{ background: p.color, color: p.textColor }}
                      >
                        <div className="flex items-center gap-3">
                          <span className="font-display font-black text-xl w-8 text-center" style={{ color: p.textColor, opacity: 0.9 }}>{p.initial}</span>
                          <div>
                            <p className="font-bold text-sm leading-tight">{p.name}</p>
                            <p className="text-[11px] opacity-70 font-normal">{p.tagline}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-semibold opacity-70 hidden sm:block">{p.eta}</span>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M7 17L17 7M17 7H7M17 7v10" />
                          </svg>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Nearest Retailer — only shown if no digital options OR as last resort */}
              {!hasQuickCommerce && !hasEcommerce && (
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--muted-foreground)' }}>
                    <span>📍</span> Nearest Retailer
                  </p>
                  {foundStores.length > 0 ? (
                    <div className="flex flex-col gap-2 max-h-56 overflow-y-auto pr-1">
                      {foundStores.map((store, i) => (
                        <div
                          key={i}
                          className="flex items-start gap-3 p-4 rounded-xl"
                          style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
                        >
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ background: 'var(--primary)', opacity: 0.9 }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--primary-foreground)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                              <polyline points="9 22 9 12 15 12 15 22" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-sm leading-tight" style={{ color: 'var(--foreground)' }}>{store.name}</p>
                            <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{store.address}, {store.city} — {store.pincode}</p>
                            {store.phone && (
                              <a href={`tel:${store.phone}`} className="text-[11px] font-semibold mt-1 inline-block transition-opacity hover:opacity-70" style={{ color: 'var(--accent-ink)' }}>
                                📞 {store.phone}
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-5 rounded-xl text-center" style={{ background: 'var(--secondary)', border: '1px dashed var(--border)' }}>
                      <p className="text-2xl mb-2">📍</p>
                      <p className="font-semibold text-sm mb-1" style={{ color: 'var(--foreground)' }}>No stockists found for {searchedPin}</p>
                      <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                        Try a nearby PIN code, or order online via Amazon or Flipkart — we deliver across India.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Retailer section shown as last resort when digital options exist */}
              {(hasQuickCommerce || hasEcommerce) && (
                <div className="pt-4" style={{ borderTop: '1px solid var(--border)' }}>
                  <p className="text-xs mb-3" style={{ color: 'var(--muted-foreground)' }}>
                    Prefer to shop locally? Find a Kasturi stockist near PIN {searchedPin}.
                  </p>
                  {foundStores.length > 0 ? (
                    <>
                      <p className="text-[10px] uppercase tracking-[0.2em] font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--muted-foreground)' }}>
                        <span>📍</span> {foundStores.length} Retailer{foundStores.length > 1 ? 's' : ''} Near You
                      </p>
                      <div className="flex flex-col gap-2 max-h-44 overflow-y-auto pr-1">
                        {foundStores.map((store, i) => (
                          <div
                            key={i}
                            className="flex items-start gap-3 p-4 rounded-xl"
                            style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
                          >
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ background: 'var(--primary)', opacity: 0.9 }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--primary-foreground)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                                <polyline points="9 22 9 12 15 12 15 22" />
                              </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-bold text-sm leading-tight" style={{ color: 'var(--foreground)' }}>{store.name}</p>
                              <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{store.address}, {store.city} — {store.pincode}</p>
                              {store.phone && (
                                <a href={`tel:${store.phone}`} className="text-[11px] font-semibold mt-1 inline-block transition-opacity hover:opacity-70" style={{ color: 'var(--accent-ink)' }}>
                                  📞 {store.phone}
                                </a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                      No retailers found for this PIN code. Order online using the options above.
                    </p>
                  )}
                </div>
              )}

              {/* Try different PIN */}
              <button
                onClick={() => { setStep('pincode'); setPincode(''); setSearchedPin(''); setFoundStores([]); }}
                className="flex items-center gap-1.5 text-xs font-semibold transition-opacity hover:opacity-70"
                style={{ color: 'var(--muted-foreground)' }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 12H5M12 5l-7 7 7 7" />
                </svg>
                Try a different PIN code
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
